<template>
  <div :class="$style.root">
    <div :class="$style.carousel_wrapper">
      <div ref="carousel" :class="$style.carousel" :style="carouselStyle">
        <div :class="$style.container" :style="carouselStyle">
          <template v-for="image in slideImages" :key="image">
            <div v-if="isVideo(image)" :class="$style.slide" @click="topImagePath = image">
              <div :class="[$style.video, { [$style.selected]: topImagePath === image }]" />
            </div>
            <div
              v-else
              :class="[$style.slide, { [$style.selected]: topImagePath === image }]"
              @click="topImagePath = image"
            >
              <AtomsConsumerImage :src="image" :width="56" :height="75" />
            </div>
          </template>
        </div>
      </div>
      <AtomsConsumerImage
        v-if="showCarouselOverlay.top"
        src="/images/common/cart/carousel_overlay.png"
        :class="[$style.overlay, $style.top]"
      />
      <AtomsConsumerImage
        v-if="showCarouselOverlay.bottom"
        src="/images/common/cart/carousel_overlay.png"
        :class="[$style.overlay, $style.bottom]"
      />
    </div>

    <div v-if="topImagePath" ref="activeImageElement" :class="$style.active_image">
      <div :class="$style.image_container">
        <video v-if="isVideo(topImagePath)" muted loop playsinline autoplay :src="topImagePath" />
        <template v-else>
          <AtomsConsumerImage :src="topImagePath" :width="184 * 3" :height="245 * 3" />
        </template>
        <AtomsConsumerButton :class="$style.expansion" @click="$emit('expandClicked', topImagePath)">
          <AtomsConsumerImage src="/images/common/cart/plus.svg" />
        </AtomsConsumerButton>
      </div>
      <div v-if="isGiftWrappingAvailableImage(topImagePath) && giftWrapped" :class="$style.promotion_label">
        <span>※ 適用中のラッピングイメージ</span>
      </div>
      <div v-else-if="isGiftWrappingAvailableImage(topImagePath)" :class="$style.promotion_label">
        <span>※ ラッピングイメージ（別売）</span>
        <span :class="$style.tips">ラッピングご希望の場合は、ギフトラッピング設定をご確認ください</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue"
import type { EmblaCarouselType } from "embla-carousel"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { isVideo } from "@tential/ec-gql-schema/utils/functions/common"

const activeImageElement = ref<HTMLDivElement>()

const props = defineProps<{
  slideImages: string[]
  /**
   * ギフトラッピング済みか（"適用中のラッピングイメージ"の表示制御を行う）
   */
  giftWrapped: boolean
  /**
   * ギフトラッピングの画像か（"ラッピングイメージ"の出しわけを行う）
   */
  isGiftWrappingAvailableImage: (path: string) => boolean
}>()

defineEmits<{
  (e: "expandClicked", url: string): void
}>()

const [carousel, emblaApi] = emblaCarouselVue(
  {
    axis: "y",
    dragFree: true,
  },
  [WheelGesturesPlugin()],
)

const topImagePath = ref<string>("")
const showCarouselOverlay = ref<{ top: boolean; bottom: boolean }>({
  top: false,
  bottom: false,
})

/**
 * カルーセルのスクロールに応じてオーバーレイの表示制御を行う
 */
const updateShowCarouselOverlay = (value: EmblaCarouselType) => {
  showCarouselOverlay.value = {
    top: value.canScrollPrev(),
    bottom: value.canScrollNext(),
  }
}

watchEffect(() => {
  if (emblaApi.value) {
    // 最初の画像選択
    topImagePath.value = props.slideImages[0]

    // オーバーレイ初回更新
    updateShowCarouselOverlay(emblaApi.value)
    emblaApi.value.on("slidesInView", updateShowCarouselOverlay)
    emblaApi.value.on("scroll", updateShowCarouselOverlay)
  }
})

const carouselStyle = ref<{ height: `${number}px` }>()
useResizeObserver(activeImageElement, (entries) => {
  const entry = entries[0]
  const { height } = entry.contentRect
  carouselStyle.value = {
    height: `${height}px`,
  }
})
</script>

<style scoped module lang="scss">
.root {
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  height: fit-content;

  .carousel_wrapper {
    position: relative;

    .carousel {
      overflow: hidden;

      .container {
        display: flex;
        gap: 0.25rem;
        flex-direction: column;

        .slide {
          flex: 0 0 auto;
          cursor: pointer;
          border-radius: 0.25rem;
          height: 75px;
          width: 56px;

          @mixin selected_color_style {
            position: absolute;
            content: "";
            width: 100%;
            height: 100%;
          }

          @mixin selected_style {
            height: 75px;
            width: 56px;
            position: relative;

            &::before {
              @include selected_color_style;
              background: rgba($primary, 0.5);
              z-index: 1;
              border-radius: 0.25rem;
            }
            &::after {
              @include selected_color_style;
              left: 0;
              background-image: url("/images/common/cart/view.svg");
              background-repeat: no-repeat;
              background-position: center;
              z-index: 2;
            }
          }

          &.selected {
            @include selected_style;
          }

          .video {
            width: 100%;
            height: 100%;
            border: 1px solid #767676;
            background-color: #f7f7f7;
            background-image: url("/images/common/button/play.svg");
            background-position: center;
            background-size: cover;
            box-sizing: border-box;

            &.selected {
              @include selected_style;
            }
          }

          img {
            border-radius: 0.25rem;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }

    .overlay {
      pointer-events: none;
      position: absolute;
      width: 100%;
      height: 24px;

      &.top {
        top: 0%;
        transform: scale(1, -1);
      }
      &.bottom {
        bottom: 0%;
      }
    }
  }

  .active_image {
    position: relative;
    flex: 1;
    height: fit-content;

    .image_container {
      position: relative;

      > img {
        aspect-ratio: 184 / 245;
        border-radius: 0.375rem;
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      > video {
        width: 100%;
      }

      .expansion {
        position: absolute;
        bottom: 10px;
        right: 10px;
        width: 36px;
        height: 36px;
        background-color: $white;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
      }
    }

    .promotion_label {
      position: absolute;
      bottom: 0.5rem;
      left: 0.5rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      line-height: 1.5rem;
      font-size: $font-size-16;
      gap: 0.25rem;
      .tips {
        font-size: $font-size-10;
      }
    }
  }
}
</style>
